
import Vue from "vue";
import {Address, AddressType, Contact, contactApi, ContactSearchRow, createContactInfos,} from "@/api/contacts";
import {Dossier} from "@/api/flame";
import ContactAddress from "@/views/contacts/ContactAddress.vue";
import {ContactRelationResult,} from "@/api/contactRelation";
import {findConfirmDialogRef} from "@/utils/RefUtils";

export interface BillingAddressFormInputDTO {
  dossier: Dossier;
  contact: Contact;
  resellerContacts: ContactRelationResult[];
}

export default Vue.extend({
  components: {
    ContactAddress
  },
  props: {
    billingAddressForm: {
      type: Object as () => BillingAddressFormInputDTO,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
  },
  data() {
    return {
      billingAddressRef: this.billingAddressForm.dossier.billingAddressRef,
      billingAddress: null as Address | null,
      newBillingAddressContactRef: null as string | null,
      dossierId: null as string | null,
      contactFromBillingAddress: null as Contact | null,
    };
  },
  computed: {
    addresses(): { value: string; text: string }[] {
      const addresses: { value: string; text: string }[] = [];
      if (this.billingAddressForm.resellerContacts && this.billingAddressForm.resellerContacts.length > 0) {
        this.billingAddressForm.resellerContacts.forEach(
            (resellerContact: ContactRelationResult) => {
              resellerContact.withContact.addresses.forEach((address) => {
                addresses.push({
                  value: address.id ?? "",
                  text:
                      createContactInfos(address.sharedByContactPersonRef ?? null, resellerContact.withContact) + " - " +
                      (address.street ? address.street : "keine Strasse") +
                      ", " +
                      (address.city ? address.city : "kein Ort") +
                      " " +
                      (address.zipCode ? address.zipCode : "keine PLZ"),
                });
              });
            }
        );
      }
      if (this.billingAddressForm.contact && this.billingAddressForm.contact.addresses) {
        this.billingAddressForm.contact.addresses.forEach((address) => {
          addresses.push({
            value: address.id ?? "",
            text:
                createContactInfos(address.sharedByContactPersonRef ?? null, this.billingAddressForm.contact) + " - " +
                (address.street ? address.street : "keine Strasse") +
                ", " +
                (address.city ? address.city : "kein Ort") +
                " " +
                (address.zipCode ? address.zipCode : "keine PLZ"),
          });
        });
      }
      return addresses;
    },
  },
  watch: {
    billingAddressForm: {
      async handler() {
        if (this.billingAddress == null || this.billingAddressForm.dossier.id !== this.dossierId) {
          this.resetData();
          if (this.billingAddressForm.dossier) {
            this.billingAddressRef = this.billingAddressForm.dossier.billingAddressRef;
            if (this.billingAddressRef !== null && this.billingAddressRef !== undefined) {
              const contactId = this.findContactIdForAddressRef(this.billingAddressRef);
              if (contactId) {
                this.contactFromBillingAddress = await contactApi.getById(contactId)
                this.billingAddress = await contactApi.getAddressById(
                    this.findContactIdForAddressRef(this.billingAddressRef) ??
                    "",
                    this.billingAddressRef
                );
              }
            }
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    resetData() {
      this.billingAddressRef = "",
          this.billingAddress = null;
      this.newBillingAddressContactRef = null;
      this.dossierId = this.billingAddressForm.dossier.id;
      this.contactFromBillingAddress = this.billingAddressForm.contact;
    },
    async setBillingAddress() {
      if (this.billingAddressRef) {
        this.billingAddress = await contactApi.getAddressById(
            this.findContactIdForAddressRef(this.billingAddressRef) ??
            "",
            this.billingAddressRef
        );
      }
      await this.updateDossier();
    },
    async updateDossier() {
      if (this.billingAddressRef) {
        this.$emit("billingAddressChanged", this.billingAddressRef);
      } else if (this.billingAddressForm.dossier.billingAddressRef !== null) {
        this.billingAddress = null;
        this.$emit("billingAddressChanged", null);
      }
    },
    findContactIdForAddressRef(addressRef: string): string | null {

      if (this.billingAddressForm.resellerContacts && this.billingAddressForm.resellerContacts.length > 0) {
        let byReseller = null;
        this.billingAddressForm.resellerContacts.forEach((reseller: ContactRelationResult) => {
          if (
              reseller.withContact.addresses.find((a) => a.id === addressRef)?.id
          ) {
            byReseller = reseller.withContact.id;
          }
        });
        if (byReseller) {
          return byReseller;
        }
      }
      if (this.billingAddressForm.contact && this.billingAddressForm.contact.addresses) {
        return this.billingAddressForm.contact.id;
      }
      return null;
    },
    changedBillingAddress(address: Address) {
      this.billingAddress = address;
      this.newBillingAddressContactRef = null;
    },
    async saveBillingAddress() {
      let contactName = this.billingAddressForm.contact.fullName;
      let contactId = this.billingAddressForm.contact.id;
      if (this.billingAddressForm.resellerContacts.length > 0 && this.newBillingAddressContactRef) {
        contactName = this.billingAddressForm.resellerContacts.find(contact => contact.id = this.newBillingAddressContactRef)?.withContact?.fullName;
        contactId = this.billingAddressForm.resellerContacts.find(contact => contact.id = this.newBillingAddressContactRef)?.withContact?.id ?? "";
      }
      const res = await (findConfirmDialogRef(this) as any).open(
          this.$t("Achtung") as string,
          this.$t(
              `Rechungsadresse aktualisieren? Adresse wird im Kontakt ${contactName} ebenfalls aktualisiert`
          ) as string,
          {
            textColor: "red",
            noconfirm: false,
          }
      );
      if (res) {
        if (this.billingAddress && this.billingAddress.id && contactId) {
          const updatedAddress = await contactApi.editAddress(
              contactId,
              this.billingAddress.id,
              this.billingAddress
          );
          this.billingAddress = {
            ...updatedAddress,
          };
        } else if (this.billingAddress && contactId) {
          this.billingAddressRef = await contactApi.addNewAddress(
              contactId,
              this.billingAddress
          );
          this.billingAddress = {
            ...this.billingAddress,
            id: this.billingAddressRef,
          };
        }
        await this.updateDossier();
      }
    },
    async addNewBillingAddressFor(contact: Contact | ContactSearchRow) {
      this.billingAddress = {addressType: AddressType.HOME} as Address;
      this.newBillingAddressContactRef = contact.id;
    },
  },
});
